import React, { useState, useEffect, useMemo } from 'react';
import { Circle, OverlayView, InfoWindow, OverlayViewF } from '@react-google-maps/api';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';

import Typography from 'src/components/ui/Typography';
import IconButton from 'src/components/ui/IconButton';

import { AdTypes, type Ad, type AdsForMapQuery } from 'src/types/__generated__/graphql';
import theme from 'src/utils/theme';
import formatPrice, { formatAdPriceForMap } from 'src/utils/formatPrice';
import { colours } from 'src/constants/colours';
import { inaccurateRadius, isHiddenRadius, circleOptions } from 'src/constants/googleMap';
import { styledTag } from 'src/utils/styled';
import SearchAdsPageMarkerCard from './SearchAdsPageMarkerCard';
import { useAdSearchState } from '../contexts/AdSearchStateContext';

type ClusterType = supercluster.PointFeature<{
    cluster: boolean;
    point_count: number;
    adId: string;
    ad: MarkerProps['ad'];
}>;

interface MarkerLabelProps {
    $isSelected: boolean;
}

const MarkerLabel = styledTag('div')<MarkerLabelProps>`
    cursor: pointer;
    background-color: ${theme.palette.primary.main};
    color: ${colours.white.main};
    border-radius: 6.25rem;
    padding: 0rem 0.75rem;
    height: 1.5rem;
    white-space: nowrap;
    display: flex;
    align-items: center;
    ${(props) =>
        props.$isSelected &&
        `
            color: ${colours.white.main};
            background-color: ${theme.palette.secondary.main};
    `}
    box-shadow: 0 0.25rem 0.5rem 0 ${colours.blackOpacity.O16};
`;

const AdsClusterSelector = styledTag('div')`
    font-size: 1rem;
    background-color: ${colours.white.main};
    color: ${colours.black.main};
    border-radius: 1rem;
    height: 2rem;
    width: 8rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 0.25rem 0.5rem 0 ${colours.blackOpacity.O16};
`;

interface MarkerContainerProps {
    $cardIsOpen: boolean;
}

const MarkerContainer = styledTag('div')<MarkerContainerProps>`
    position: relative;
    width: fit-content;
    height: fit-content;
    transform: translate(-50%, -50%);
    --box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
        0px 1px 5px 0px rgba(0, 0, 0, 0.12);

    position: relative;
    ${(props) =>
        props.$cardIsOpen &&
        `
        /* Add an invisible element between the marker and the card
        so focus is not lost while moving the mouse to the card */
        &::after {
            content: '';
            position: absolute;
            top: -1rem;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: -1;
        }
    `}

    .marker-label {
        ${(props) =>
            props.$cardIsOpen &&
            `
            color: ${colours.white.main};
            background-color: ${theme.palette.secondary.main};
        `}
    }
`;

const ClusterMarkerContainer = styledTag('div')<{ $hoveredAdIsOnCluster: boolean }>`
    transform: translate(-50%, -50%);
    display: flex;
    width: 2rem;
    height: 2rem;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 6.25rem;
    padding: 0.5rem;
    background: ${({ $hoveredAdIsOnCluster }) =>
        $hoveredAdIsOnCluster ? colours.secondary.main : colours.primary.main};
    :hover {
        cursor: pointer;
        ${({ $hoveredAdIsOnCluster }) =>
            !$hoveredAdIsOnCluster &&
            `
            background: ${colours.primary.O87};
        `}
    }
    box-shadow: 0 0.25rem 0.5rem 0 ${colours.blackOpacity.O16};
    position: relative;
    ${(props) =>
        props.$hoveredAdIsOnCluster &&
        `
        /* Add an invisible element between the marker and the card
        so focus is not lost while moving the mouse to the card */
        &::after {
            content: '';
            position: absolute;
            top: -1rem;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: -1;
            width: 520%;
        }
    `}
`;

export type MarkerProps = {
    ad?: ArrayElement<NonNullable<AdsForMapQuery['ads']>['items']>;
    cluster?: {
        lat: number;
        lng: number;
        pointCount: number;
        id?: string | number;
        childrenIds?: string[];
        mapIsOnMaxZoom: boolean;
        handleClickCluster: () => void;
        getClusterChildren: () => ClusterType[] | null;
    };
};

export const SearchAdsPageMarker: React.FC<MarkerProps> = ({ ad, cluster }) => {
    const { t, i18n } = useTranslation(['ad']);
    const [clusterAds, setClusterAds] = useState<ClusterType[] | null>(null);
    const { setHoveredAd, hoveredAdData } = useAdSearchState();

    const hoveredAdIsOnCluster = Boolean(cluster && hoveredAdData && cluster.childrenIds?.includes(hoveredAdData.id));
    const infoViewIsOpen = Boolean((ad && hoveredAdData?.id === ad.id) || (hoveredAdIsOnCluster && hoveredAdData));

    const marker = useMemo(() => {
        if (!ad) return null;

        return (
            <OverlayViewF
                key={`marker-${ad.id}`}
                position={{ lat: ad.proxyLat, lng: ad.proxyLng }}
                mapPaneName={infoViewIsOpen ? OverlayView.FLOAT_PANE : OverlayView.OVERLAY_MOUSE_TARGET} // this allows the opened card to be top of layer
            >
                <MarkerContainer
                    $cardIsOpen={infoViewIsOpen}
                    onMouseEnter={() => setHoveredAd(ad)}
                    onMouseLeave={() => setHoveredAd(null)}
                    onClick={(e: React.MouseEvent) => {
                        // this allows to click on marker and open the InfoWindow it's really usefull in mobile resolution
                        e.stopPropagation();
                        if (infoViewIsOpen) {
                            setHoveredAd(null);
                        } else {
                            setHoveredAd(ad);
                        }
                    }}
                    style={infoViewIsOpen && ad.isInaccurateAddress ? { zIndex: 10 } : {}}
                >
                    <MarkerLabel $isSelected={infoViewIsOpen} className="marker-label">
                        <Typography
                            variant="h6"
                            color={colours.white.main}
                            lineHeight="1.125rem"
                            letterSpacing="0.01313rem"
                        >
                            {ad.type === AdTypes.Sell
                                ? formatAdPriceForMap({
                                      price: Number(ad.price),
                                      lang: i18n.language,
                                  })
                                : formatPrice({
                                      price: Number(ad.rent),
                                      lang: i18n.language,
                                      notation: 'standard',
                                  })}
                        </Typography>
                    </MarkerLabel>
                </MarkerContainer>
                {infoViewIsOpen && ad.isInaccurateAddress && (
                    <Box mt="0.3rem" width="fit-content" style={{ transform: 'translate(-50%, -50%)' }}>
                        <MarkerLabel $isSelected={false}>{t('ad:inaccurateAddress')}</MarkerLabel>
                    </Box>
                )}
            </OverlayViewF>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ad, i18n.language, infoViewIsOpen, t]);

    const visibleClusterAdIndex =
        hoveredAdData && clusterAds?.findIndex((clusterAd) => clusterAd.properties.adId === hoveredAdData.id);
    const clusterAdIsVisible = typeof visibleClusterAdIndex === 'number' && visibleClusterAdIndex >= 0;

    useEffect(() => {
        if (cluster) {
            if (cluster.mapIsOnMaxZoom && !clusterAds) {
                const children = cluster.getClusterChildren();
                setClusterAds(children);
                if (children?.length) {
                    setHoveredAd(children[0].properties.ad);
                } else {
                    setHoveredAd(null);
                }
            } else if (!cluster.mapIsOnMaxZoom) {
                setClusterAds(null);
            }
        }
    }, [cluster, cluster?.mapIsOnMaxZoom, clusterAds, setHoveredAd]);

    const clusterMarker = useMemo(() => {
        const handleClickPrevNextAdsClusterSelector = (prevOrNex: 'prev' | 'next') => {
            if (clusterAdIsVisible && clusterAds) {
                const newIndex = prevOrNex === 'prev' ? visibleClusterAdIndex - 1 : visibleClusterAdIndex + 1;
                const newVisibleAd = clusterAds[newIndex].properties.ad;
                setHoveredAd(newVisibleAd, true);
            }
        };

        if (!cluster) return null;
        return (
            <OverlayViewF
                key={`cluster-${cluster.id}`}
                position={{ lat: cluster.lat, lng: cluster.lng }}
                mapPaneName={clusterAdIsVisible ? OverlayView.FLOAT_PANE : OverlayView.OVERLAY_MOUSE_TARGET} // this allows the opened card to be top of layer
            >
                <div>
                    <ClusterMarkerContainer
                        $hoveredAdIsOnCluster={hoveredAdIsOnCluster}
                        onClick={(e) => {
                            e.stopPropagation();
                            cluster.handleClickCluster();
                        }}
                        onMouseEnter={() => {
                            if (!hoveredAdData && clusterAds && cluster.mapIsOnMaxZoom) {
                                setHoveredAd(clusterAds[0].properties.ad);
                            }
                            if (clusterAdIsVisible) {
                                setHoveredAd(hoveredAdData, true);
                            }
                        }}
                        onMouseLeave={() => {
                            if (clusterAdIsVisible) {
                                setHoveredAd(null);
                            }
                        }}
                    >
                        <Typography
                            variant="h6"
                            color={colours.white.main}
                            fontSize="0.75rem"
                            lineHeight="0.875rem"
                            letterSpacing="0.01313rem"
                        >
                            {cluster.pointCount}
                        </Typography>
                    </ClusterMarkerContainer>
                    {clusterAds && hoveredAdData && clusterAdIsVisible && (
                        <Box
                            mt="0.3rem"
                            width="fit-content"
                            style={{ transform: 'translate(30%, -172%)' }}
                            onMouseEnter={() => {
                                setHoveredAd(hoveredAdData, true);
                            }}
                            onMouseLeave={() => setHoveredAd(null)}
                        >
                            <AdsClusterSelector>
                                <IconButton
                                    color="white"
                                    onClick={() => handleClickPrevNextAdsClusterSelector('prev')}
                                    iconProps={{
                                        name: 'ChevronLeft',
                                        size: '1rem',
                                    }}
                                    disabled={visibleClusterAdIndex === 0}
                                />
                                <Typography variant="h5" color={colours.black.main} fontWeight={400}>
                                    {`${visibleClusterAdIndex + 1} / ${clusterAds.length}`}
                                </Typography>
                                <IconButton
                                    color="white"
                                    onClick={() => handleClickPrevNextAdsClusterSelector('next')}
                                    iconProps={{
                                        name: 'ChevronRight',
                                        size: '1rem',
                                    }}
                                    disabled={visibleClusterAdIndex === clusterAds.length - 1}
                                />
                            </AdsClusterSelector>
                        </Box>
                    )}
                </div>
            </OverlayViewF>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cluster, clusterAdIsVisible, clusterAds, hoveredAdData, hoveredAdIsOnCluster, visibleClusterAdIndex]);

    const adPreview = useMemo(() => {
        if (!hoveredAdData || !infoViewIsOpen) return null;

        return (
            <div
                onMouseEnter={() => setHoveredAd(hoveredAdData)}
                onMouseLeave={() => setHoveredAd(null)}
                style={{ borderRadius: '1rem' }}
            >
                {/* Style for InfoWindow is injected in global.css file */}
                <InfoWindow
                    key={`infowindow-${hoveredAdData.id}`}
                    position={
                        {
                            lat: hoveredAdData.proxyLat,
                            lng: hoveredAdData.proxyLng,
                        } as unknown as google.maps.LatLng
                    }
                    options={{
                        disableAutoPan: true,
                        pixelOffset: new google.maps.Size(0, clusterAdIsVisible ? -15 : -8),
                        ariaLabel: 'ad-info-window', // this is used to style this specific info window and not all so don't change this
                    }}
                    onLoad={(infoWindow: google.maps.InfoWindow) => infoWindow}
                >
                    <SearchAdsPageMarkerCard ad={hoveredAdData as Ad} />
                </InfoWindow>
            </div>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clusterAdIsVisible, hoveredAdData, infoViewIsOpen]);

    const adCircleArea = useMemo(() => {
        if (infoViewIsOpen && (hoveredAdData?.isInaccurateAddress || hoveredAdData?.isHiddingCompleteAddress)) {
            return (
                <Circle
                    key={hoveredAdData.id}
                    center={{
                        lat: hoveredAdData.proxyLat,
                        lng: hoveredAdData.proxyLng,
                    }}
                    radius={hoveredAdData.isInaccurateAddress ? inaccurateRadius : isHiddenRadius}
                    options={circleOptions}
                />
            );
        }
        return null;
    }, [hoveredAdData, infoViewIsOpen]);

    return (
        <>
            {marker}
            {clusterMarker}
            {adPreview}
            {adCircleArea}
        </>
    );
};
